import { useEffect, useState } from "react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { Alert, Box, Button, Chip, Container, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { QuoteController } from "../controllers/quote";
import { useAuth } from "../context/AuthContext";

export default function Quote() {
  const { id } = useParams();
  const { user, loading } = useAuth();
  const [q, setQ] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (!user) return;
    QuoteController.get(id).then(setQ).catch((e) => setErr(e.message));
  }, [user, id]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;

  async function checkout() {
    setBusy(true); setErr(null);
    try {
      const { order, bolt } = await QuoteController.checkout(id);
      if (bolt?.token) {
        nav(`/checkout?bolt=${encodeURIComponent(bolt.token)}&order=${encodeURIComponent(order?.id || "")}`);
      } else {
        nav(`/orders/${order.id}`);
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (err) return <Container sx={{ py: 6 }}><Alert severity="error">{err}</Alert></Container>;
  if (!q) return <Container sx={{ py: 6 }}><Typography>Loading quote…</Typography></Container>;

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="h2" sx={{ fontSize: "1.8rem" }}>Quote {q.number || q.id}</Typography>
        <Chip label={q.status || "open"} />
      </Stack>
      <Paper sx={{ mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>SKU</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell align="right">Unit</TableCell>
              <TableCell align="right">Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(q.lines || q.items || []).map((l, i) => (
              <TableRow key={i}>
                <TableCell>{l.sku}</TableCell>
                <TableCell>{l.description}</TableCell>
                <TableCell align="right">{l.qty}</TableCell>
                <TableCell align="right">{l.unitPrice ? `$${Number(l.unitPrice).toFixed(2)}` : "—"}</TableCell>
                <TableCell align="right">{l.total ? `$${Number(l.total).toFixed(2)}` : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      <Box sx={{ textAlign: "right" }}>
        <Typography variant="h3" sx={{ fontSize: "1.2rem", mb: 2 }}>
          Total: {q.total ? `$${Number(q.total).toFixed(2)}` : "—"}
        </Typography>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="outlined" onClick={() => nav("/quotes")}>Back</Button>
          <Button variant="contained" onClick={checkout} disabled={busy}>
            {busy ? "Processing…" : "Convert to Order & Checkout"}
          </Button>
        </Stack>
      </Box>
    </Container>
  );
}
